import HttpResponse from 'core/net/HttpResponse';
import { httpClient } from 'core/net/http';
import { LogManager } from 'core/logging/LogManager';
import { DateTime } from 'luxon';
import * as v from 'valibot';
import { BlastModelType } from 'app/business-logic/domain-models/Blasting';
import {
  DispersionSimulationDataSchema,
  FlyrockSimulationDataSchema,
  FumeManagementZoneSimulationDataSchema,
  GroundVibrationSimulationDataSchema,
  OverpressureSimulationDataSchema,
  PathSimulationDataSchema,
} from 'app/business-logic/domain-models/Blasting.schema';

const logger = LogManager.getLogger('fetchBlastSimulationData', { error: { logToServer: true } });

type Schema =
  | typeof DispersionSimulationDataSchema
  | typeof FlyrockSimulationDataSchema
  | typeof FumeManagementZoneSimulationDataSchema
  | typeof GroundVibrationSimulationDataSchema
  | typeof OverpressureSimulationDataSchema
  | typeof PathSimulationDataSchema;

type RTS<S extends Schema> = ReturnType<typeof handleResponse<S>>;

type Type<S extends Schema> = NonNullable<v.InferOutput<S>['modelData']>['modelType'];

type RT<T extends BlastModelType> = T extends BlastModelType.Dispersion
  ? RTS<typeof DispersionSimulationDataSchema>
  : T extends BlastModelType.Flyrock
    ? RTS<typeof FlyrockSimulationDataSchema>
    : T extends BlastModelType.FumeManagementZone
      ? RTS<typeof FumeManagementZoneSimulationDataSchema>
      : T extends BlastModelType.GroundVibration
        ? RTS<typeof GroundVibrationSimulationDataSchema>
        : T extends BlastModelType.OverpressureContour
          ? RTS<typeof OverpressureSimulationDataSchema>
          : T extends BlastModelType.OverpressureGain
            ? RTS<typeof OverpressureSimulationDataSchema>
            : T extends BlastModelType.Path
              ? RTS<typeof PathSimulationDataSchema>
              : never;

export async function fetchBlastSimulationData(
  alias: string | undefined,
  blastTime: DateTime,
  modelType: BlastModelType.Dispersion
): Promise<RT<BlastModelType.Dispersion>>;
export async function fetchBlastSimulationData(
  alias: string | undefined,
  blastTime: DateTime,
  modelType: BlastModelType.Flyrock
): Promise<RT<BlastModelType.Flyrock>>;
export async function fetchBlastSimulationData(
  alias: string | undefined,
  blastTime: DateTime,
  modelType: BlastModelType.FumeManagementZone
): Promise<RT<BlastModelType.FumeManagementZone>>;
export async function fetchBlastSimulationData(
  alias: string | undefined,
  blastTime: DateTime,
  modelType: BlastModelType.GroundVibration
): Promise<RT<BlastModelType.GroundVibration>>;
export async function fetchBlastSimulationData(
  alias: string | undefined,
  blastTime: DateTime,
  modelType: BlastModelType.OverpressureContour
): Promise<RT<BlastModelType.OverpressureContour>>;
export async function fetchBlastSimulationData(
  alias: string | undefined,
  blastTime: DateTime,
  modelType: BlastModelType.OverpressureGain
): Promise<RT<BlastModelType.OverpressureGain>>;
export async function fetchBlastSimulationData(
  alias: string | undefined,
  blastTime: DateTime,
  modelType: BlastModelType.Path
): Promise<RT<BlastModelType.Path>>;
export async function fetchBlastSimulationData(
  alias: string | undefined,
  blastTime: DateTime,
  modelType: BlastModelType.Vibration
): Promise<RT<BlastModelType.Vibration>>;
export async function fetchBlastSimulationData(
  alias: string | undefined,
  blastTime: DateTime,
  modelType: BlastModelType
): Promise<RT<BlastModelType>> {
  const response = await httpClient.useInternalApi
    .asAuthorised()
    .get(`/blasting/scenarios/${alias}/${modelType}`, { utcBlastTime: blastTime.toISO() });

  if (!response.isSuccess) {
    const errorMessage = `Failed to fetch blast simulation data for model type "${modelType}", alias "${alias}", blast time "${blastTime.toISO()}"`;
    logger.error(errorMessage);
    throw new Error(errorMessage);
  }

  switch (modelType) {
    case BlastModelType.Dispersion: {
      return handleResponse({
        schema: DispersionSimulationDataSchema,
        response,
        params: { alias, blastTime, modelType },
      });
    }
    case BlastModelType.Flyrock: {
      return handleResponse({ schema: FlyrockSimulationDataSchema, response, params: { alias, blastTime, modelType } });
    }
    case BlastModelType.FumeManagementZone: {
      return handleResponse({
        schema: FumeManagementZoneSimulationDataSchema,
        response,
        params: { alias, blastTime, modelType },
      });
    }
    case BlastModelType.GroundVibration: {
      return handleResponse({
        schema: GroundVibrationSimulationDataSchema,
        response,
        params: { alias, blastTime, modelType },
      });
    }
    case BlastModelType.OverpressureContour: {
      return handleResponse({
        schema: OverpressureSimulationDataSchema,
        response,
        params: { alias, blastTime, modelType },
      });
    }
    case BlastModelType.OverpressureGain: {
      return handleResponse({
        schema: OverpressureSimulationDataSchema,
        response,
        params: { alias, blastTime, modelType },
      });
    }
    case BlastModelType.Path: {
      return handleResponse({
        schema: PathSimulationDataSchema,
        response,
        params: { alias, blastTime, modelType },
      });
    }
    default: {
      const errorMessage = `Failed to handle blast model type "${modelType}" for alias "${alias}" and blast time "${blastTime.toISO()}"`;
      logger.error(errorMessage);
      throw new Error(errorMessage);
    }
  }
}

function handleResponse<S extends Schema>({
  schema,
  response,
  params,
}: {
  schema: S;
  response: HttpResponse;
  params: {
    modelType: Type<S>;
    alias: string | undefined;
    blastTime: DateTime;
  };
}): {
  type: Type<S>;
  result: v.InferOutput<S>;
} {
  const parsedResponse = v.safeParse(schema, response.data);
  if (!parsedResponse.success) {
    const errorMessage = `Failed to parse blast simulation data response for alias "${params.alias}", blast time "${params.blastTime.toISO()}", model type "${params.modelType}"`;
    logger.error(errorMessage, parsedResponse.issues);
    throw new Error(errorMessage);
  }
  return {
    type: params.modelType,
    result: parsedResponse.output,
  };
}
